import React, { useState, useEffect, useCallback } from "react";
import {
  Tabs, Table, Button, Modal, Form, Input, Select, Switch, InputNumber,
  Tag, Space, message, Tooltip, Popconfirm, Card, Row, Col, Statistic,
  Spin, Empty, Radio, Alert
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, RobotOutlined,
  QuestionCircleOutlined, BookOutlined, FolderOutlined,
  RocketOutlined, FileTextOutlined, ReloadOutlined, EyeOutlined,
  UserOutlined, TeamOutlined, CloudSyncOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import HelpCenter from "../../Customer/HelpCenter/HelpCenter";

const { TextArea } = Input;
const { Option } = Select;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 8px 0; }
  p { color: #666; margin: 0; font-size: 14px; }
`;

const StyledCard = styled(Card)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  margin-bottom: 24px;
  .ant-card-body { padding: 20px; }
`;

const AIPanel = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;

  .ai-title {
    font-weight: 600;
    color: #0369a1;
    font-size: 14px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const ICON_OPTIONS = [
  "QuestionCircleOutlined",
  "BookOutlined",
  "RocketOutlined",
  "FileTextOutlined",
  "FolderOutlined",
  "SettingOutlined",
  "TeamOutlined",
  "DollarOutlined",
  "ClockCircleOutlined",
  "SearchOutlined",
];

const VISIBLE_TO_OPTIONS = [
  { value: "both", label: "Both (Customer & Partner)" },
  { value: "customer", label: "Customer Only" },
  { value: "partner", label: "Partner Only" },
];

const AdminHelpCenter = () => {
  const [activeTab, setActiveTab] = useState("categories");
  const [previewRole, setPreviewRole] = useState("customer");
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catModalVisible, setCatModalVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemType, setItemType] = useState("article");
  const [catForm] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiContext, setAiContext] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  const token = localStorage.getItem("authToken");
  const headers = { Authorization: `Bearer ${token}` };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(API_CONST.HELP_SEED, {}, { headers });
      if (res.data.success) {
        message.success("Seed applied — all default categories and articles are now up to date.");
        fetchStats();
        fetchCategories();
        fetchArticles();
        fetchFaqs();
      }
    } catch (e) {
      message.error(e.response?.data?.error || "Seed failed. Check server logs.");
    } finally {
      setSeeding(false);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(API_CONST.HELP_GET_STATS, { headers });
      if (res.data.success) setStats(res.data.stats);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_CONST.HELP_LIST_CATEGORIES, { headers });
      if (res.data.success) setCategories(res.data.categories);
    } catch (e) { message.error("Failed to load categories"); }
    finally { setLoading(false); }
  }, []);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(API_CONST.HELP_LIST_ITEMS, { type: "article", limit: 100 }, { headers });
      if (res.data.success) setArticles(res.data.items);
    } catch (e) { message.error("Failed to load articles"); }
    finally { setLoading(false); }
  }, []);

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(API_CONST.HELP_LIST_ITEMS, { type: "faq", limit: 100 }, { headers });
      if (res.data.success) setFaqs(res.data.items);
    } catch (e) { message.error("Failed to load FAQs"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchCategories();
    fetchArticles();
    fetchFaqs();
  }, []);

  const openCatModal = (cat = null) => {
    setEditingCat(cat);
    catForm.setFieldsValue(cat ? {
      name: cat.name,
      icon: cat.icon,
      description: cat.description,
      visibleTo: cat.visibleTo,
      sortOrder: cat.sortOrder,
    } : { icon: "QuestionCircleOutlined", visibleTo: "both", sortOrder: 0 });
    setCatModalVisible(true);
  };

  const handleSaveCategory = async () => {
    try {
      const values = await catForm.validateFields();
      setSaving(true);
      if (editingCat) {
        await axios.put(`${API_CONST.HELP_UPDATE_CATEGORY}/${editingCat.id}`, values, { headers });
        message.success("Category updated");
      } else {
        await axios.post(API_CONST.HELP_CREATE_CATEGORY, values, { headers });
        message.success("Category created");
      }
      setCatModalVisible(false);
      catForm.resetFields();
      setEditingCat(null);
      fetchCategories();
      fetchStats();
    } catch (e) {
      if (!e.errorFields) message.error(e.response?.data?.error || "Failed to save category");
    } finally { setSaving(false); }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API_CONST.HELP_DELETE_CATEGORY}/${id}`, { headers });
      message.success("Category deleted");
      fetchCategories();
      fetchStats();
    } catch (e) { message.error(e.response?.data?.error || "Failed to delete category"); }
  };

  const openItemModal = (item = null, type = "article") => {
    setEditingItem(item);
    setItemType(type);
    itemForm.setFieldsValue(item ? {
      title: item.title,
      content: item.content,
      categoryId: item.categoryId || "",
      visibleTo: item.visibleTo,
      isGettingStarted: item.isGettingStarted,
      sortOrder: item.sortOrder,
      status: item.status,
      tags: item.tags?.join(", ") || "",
    } : {
      type,
      visibleTo: "both",
      isGettingStarted: false,
      sortOrder: 0,
      status: "draft",
    });
    setAiTopic("");
    setAiContext("");
    setItemModalVisible(true);
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      message.warning("Please enter a topic or question for AI generation");
      return;
    }
    setAiGenerating(true);
    try {
      const res = await axios.post(API_CONST.HELP_GENERATE_CONTENT, {
        topic: aiTopic,
        type: itemType,
        platformContext: aiContext || undefined,
      }, { headers });
      if (res.data.success) {
        const { title, content } = res.data.content;
        itemForm.setFieldsValue({ title, content });
        message.success("Content generated! Review and edit before saving.");
      }
    } catch (e) {
      message.error(e.response?.data?.error || "AI generation failed");
    } finally { setAiGenerating(false); }
  };

  const handleSaveItem = async () => {
    try {
      const values = await itemForm.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        type: editingItem ? editingItem.type : itemType,
        categoryId: values.categoryId || undefined,
        tags: values.tags ? values.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      if (editingItem) {
        await axios.put(`${API_CONST.HELP_UPDATE_ITEM}/${editingItem.id}`, payload, { headers });
        message.success(`${itemType === "article" ? "Article" : "FAQ"} updated`);
      } else {
        await axios.post(API_CONST.HELP_CREATE_ITEM, payload, { headers });
        message.success(`${itemType === "article" ? "Article" : "FAQ"} created`);
      }
      setItemModalVisible(false);
      itemForm.resetFields();
      setEditingItem(null);
      if (itemType === "article") fetchArticles();
      else fetchFaqs();
      fetchStats();
    } catch (e) {
      if (!e.errorFields) message.error(e.response?.data?.error || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDeleteItem = async (id, type) => {
    try {
      await axios.delete(`${API_CONST.HELP_DELETE_ITEM}/${id}`, { headers });
      message.success(`${type === "article" ? "Article" : "FAQ"} deleted`);
      if (type === "article") fetchArticles();
      else fetchFaqs();
      fetchStats();
    } catch (e) { message.error(e.response?.data?.error || "Failed to delete"); }
  };

  const getVisibleToTag = (v) => {
    const colors = { customer: "blue", partner: "purple", both: "green" };
    const labels = { customer: "Customer", partner: "Partner", both: "Both" };
    return <Tag color={colors[v]}>{labels[v] || v}</Tag>;
  };

  const catColumns = [
    { title: "Name", dataIndex: "name", key: "name", render: (n, r) => <span><strong>{n}</strong></span> },
    { title: "Icon", dataIndex: "icon", key: "icon", width: 120 },
    { title: "Visible To", dataIndex: "visibleTo", key: "visibleTo", width: 130, render: getVisibleToTag },
    { title: "Sort Order", dataIndex: "sortOrder", key: "sortOrder", width: 100 },
    { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
    {
      title: "Actions", key: "actions", width: 100,
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openCatModal(r)} /></Tooltip>
          <Popconfirm title="Delete this category?" onConfirm={() => handleDeleteCategory(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const articleColumns = [
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true, render: t => <strong>{t}</strong> },
    {
      title: "Category", dataIndex: "categoryId", key: "categoryId", width: 130,
      render: cid => {
        const cat = categories.find(c => c.id === cid);
        return cat ? <Tag>{cat.name}</Tag> : <Tag color="default">Generic</Tag>;
      }
    },
    { title: "Visible To", dataIndex: "visibleTo", key: "visibleTo", width: 130, render: getVisibleToTag },
    {
      title: "Status", dataIndex: "status", key: "status", width: 100,
      render: s => <Tag color={s === "published" ? "green" : "orange"}>{s?.toUpperCase()}</Tag>
    },
    {
      title: "Getting Started", dataIndex: "isGettingStarted", key: "isGettingStarted", width: 130,
      render: v => v ? <Tag color="blue">Yes</Tag> : null
    },
    {
      title: "Actions", key: "actions", width: 100,
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openItemModal(r, "article")} /></Tooltip>
          <Popconfirm title="Delete this article?" onConfirm={() => handleDeleteItem(r.id, "article")} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const faqColumns = [
    { title: "Question", dataIndex: "title", key: "title", ellipsis: true, render: t => <strong>{t}</strong> },
    {
      title: "Category", dataIndex: "categoryId", key: "categoryId", width: 130,
      render: cid => {
        const cat = categories.find(c => c.id === cid);
        return cat ? <Tag>{cat.name}</Tag> : <Tag color="default">Generic (Homepage)</Tag>;
      }
    },
    { title: "Visible To", dataIndex: "visibleTo", key: "visibleTo", width: 130, render: getVisibleToTag },
    {
      title: "Status", dataIndex: "status", key: "status", width: 100,
      render: s => <Tag color={s === "published" ? "green" : "orange"}>{s?.toUpperCase()}</Tag>
    },
    {
      title: "Actions", key: "actions", width: 100,
      render: (_, r) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openItemModal(r, "faq")} /></Tooltip>
          <Popconfirm title="Delete this FAQ?" onConfirm={() => handleDeleteItem(r.id, "faq")} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "categories",
      label: <span><FolderOutlined /> Categories ({categories.length})</span>,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "#64748b", fontSize: 13 }}>Organize your help content into categories</span>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchCategories} />
              <Button type="primary" icon={<PlusOutlined />} style={{ background: "#014c75" }} onClick={() => openCatModal()}>
                Add Category
              </Button>
            </Space>
          </div>
          <Table
            columns={catColumns}
            dataSource={categories}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="middle"
            locale={{ emptyText: <Empty description="No categories yet. Add one to get started." /> }}
          />
        </div>
      ),
    },
    {
      key: "articles",
      label: <span><BookOutlined /> Articles {stats && <Tag style={{ marginLeft: 4 }}>{stats.articles?.published}/{stats.articles?.total}</Tag>}</span>,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "#64748b", fontSize: 13 }}>Manage help articles and documentation</span>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchArticles} />
              <Button type="primary" icon={<PlusOutlined />} style={{ background: "#014c75" }} onClick={() => openItemModal(null, "article")}>
                Add Article
              </Button>
            </Space>
          </div>
          <Table
            columns={articleColumns}
            dataSource={articles}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="middle"
            locale={{ emptyText: <Empty description="No articles yet." /> }}
          />
        </div>
      ),
    },
    {
      key: "faqs",
      label: <span><QuestionCircleOutlined /> FAQs {stats && <Tag style={{ marginLeft: 4 }}>{stats.faqs?.published}/{stats.faqs?.total}</Tag>}</span>,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ color: "#64748b", fontSize: 13 }}>Manage frequently asked questions. FAQs without a category appear on the homepage.</span>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchFaqs} />
              <Button type="primary" icon={<PlusOutlined />} style={{ background: "#014c75" }} onClick={() => openItemModal(null, "faq")}>
                Add FAQ
              </Button>
            </Space>
          </div>
          <Table
            columns={faqColumns}
            dataSource={faqs}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            size="middle"
            locale={{ emptyText: <Empty description="No FAQs yet." /> }}
          />
        </div>
      ),
    },
    {
      key: "preview",
      label: <span><EyeOutlined /> Preview User View</span>,
      children: (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 20, borderRadius: 8 }}
            message="Admin Preview Mode"
            description="This shows how the Help Center appears to users. Use the toggle below to switch between Customer and Partner views. Content is filtered by visibility settings."
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <span style={{ fontWeight: 600, color: "#374151" }}>Viewing as:</span>
            <Radio.Group
              value={previewRole}
              onChange={e => setPreviewRole(e.target.value)}
              buttonStyle="solid"
              size="middle"
            >
              <Radio.Button value="customer">
                <UserOutlined style={{ marginRight: 6 }} />Customer
              </Radio.Button>
              <Radio.Button value="partner">
                <TeamOutlined style={{ marginRight: 6 }} />Partner
              </Radio.Button>
            </Radio.Group>
            <span style={{ color: "#94a3b8", fontSize: 12 }}>
              Showing content visible to <strong>{previewRole === "customer" ? "Customers" : "Partners"}</strong> and content marked "Both"
            </span>
          </div>
          <div style={{
            border: "2px dashed #cbd5e1",
            borderRadius: 12,
            padding: "24px 24px 0",
            background: "#f8fafc",
            minHeight: 400,
          }}>
            <HelpCenter forceRole={previewRole} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1>Help Center</h1>
            <p>Manage help articles, FAQs, and categories for Customers and Partners</p>
          </div>
          <Popconfirm
            title="Re-seed default content?"
            description="This will upsert all built-in categories and articles. Existing custom content is not affected."
            onConfirm={handleSeed}
            okText="Seed Now"
            cancelText="Cancel"
            okButtonProps={{ style: { background: "#014c75" } }}
          >
            <Button
              icon={<CloudSyncOutlined />}
              loading={seeding}
              style={{ marginTop: 4 }}
            >
              Seed Default Content
            </Button>
          </Popconfirm>
        </div>
      </PageHeader>

      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <StyledCard><Statistic title="Categories" value={stats.categories} prefix={<FolderOutlined style={{ color: "#0369a1" }} />} /></StyledCard>
          </Col>
          <Col xs={12} sm={6}>
            <StyledCard><Statistic title="Articles Published" value={stats.articles?.published} suffix={`/ ${stats.articles?.total}`} prefix={<BookOutlined style={{ color: "#10b981" }} />} /></StyledCard>
          </Col>
          <Col xs={12} sm={6}>
            <StyledCard><Statistic title="FAQs Published" value={stats.faqs?.published} suffix={`/ ${stats.faqs?.total}`} prefix={<QuestionCircleOutlined style={{ color: "#f59e0b" }} />} /></StyledCard>
          </Col>
          <Col xs={12} sm={6}>
            <StyledCard><Statistic title="Draft Items" value={(stats.articles?.draft || 0) + (stats.faqs?.draft || 0)} prefix={<FileTextOutlined style={{ color: "#94a3b8" }} />} /></StyledCard>
          </Col>
        </Row>
      )}

      <Card
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ padding: "0 24px" }}
          tabBarStyle={{ marginBottom: 0, paddingTop: 8 }}
        />
      </Card>

      <Modal
        title={editingCat ? "Edit Category" : "Add Category"}
        open={catModalVisible}
        onOk={handleSaveCategory}
        onCancel={() => { setCatModalVisible(false); catForm.resetFields(); setEditingCat(null); }}
        confirmLoading={saving}
        okText={editingCat ? "Update" : "Create"}
        okButtonProps={{ style: { background: "#014c75" } }}
        width={500}
      >
        <Form form={catForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Category Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="e.g. Getting Started, Billing & Finance" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="icon" label="Icon" rules={[{ required: true }]}>
                <Select>
                  {ICON_OPTIONS.map(i => <Option key={i} value={i}>{i}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="visibleTo" label="Visible To" rules={[{ required: true }]}>
                <Select>
                  {VISIBLE_TO_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of this category" />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort Order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <span>
            {editingItem ? "Edit" : "Add"} {itemType === "article" ? "Article" : "FAQ"}
          </span>
        }
        open={itemModalVisible}
        onOk={handleSaveItem}
        onCancel={() => { setItemModalVisible(false); itemForm.resetFields(); setEditingItem(null); setAiTopic(""); setAiContext(""); }}
        confirmLoading={saving}
        okText={editingItem ? "Update" : "Create"}
        okButtonProps={{ style: { background: "#014c75" } }}
        width={720}
      >
        <div style={{ marginTop: 16 }}>
          <AIPanel>
            <div className="ai-title">
              <RobotOutlined /> AI Content Generation
            </div>
            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={16}>
                <Input
                  placeholder={itemType === "article" ? "Topic: e.g. How to post a job requirement" : "Question: e.g. How do I reset my password?"}
                  value={aiTopic}
                  onChange={e => setAiTopic(e.target.value)}
                />
              </Col>
              <Col span={8}>
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  loading={aiGenerating}
                  onClick={handleAIGenerate}
                  style={{ width: "100%", background: "#0369a1" }}
                >
                  {aiGenerating ? "Generating..." : "Generate with AI"}
                </Button>
              </Col>
            </Row>
            <Input
              placeholder="Optional: Add context to help AI generate better content (e.g. target audience, specific scenario)"
              value={aiContext}
              onChange={e => setAiContext(e.target.value)}
              size="small"
              style={{ background: "rgba(255,255,255,0.7)" }}
            />
          </AIPanel>

          <Form form={itemForm} layout="vertical">
            <Form.Item
              name="title"
              label={itemType === "article" ? "Article Title" : "Question"}
              rules={[{ required: true, message: "Title/Question is required" }]}
            >
              <Input placeholder={itemType === "article" ? "e.g. How to Post a Job Requirement" : "e.g. How do I submit talent profiles to a job?"} />
            </Form.Item>

            <Form.Item
              name="content"
              label={itemType === "article" ? "Article Content" : "Answer"}
              rules={[{ required: true, message: "Content is required" }]}
            >
              <TextArea
                rows={8}
                placeholder={itemType === "article" ? "Article body (supports markdown-style headings with ## and bullet lists with -)" : "Concise, accurate answer to the question"}
              />
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="categoryId" label="Category">
                  <Select allowClear placeholder={itemType === "faq" ? "Leave blank for generic FAQ (homepage)" : "Select category"}>
                    {categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="visibleTo" label="Visible To" rules={[{ required: true }]}>
                  <Select>
                    {VISIBLE_TO_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                  <Select>
                    <Option value="draft">Draft</Option>
                    <Option value="published">Published</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="sortOrder" label="Sort Order">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              {itemType === "article" && (
                <Col span={8}>
                  <Form.Item name="isGettingStarted" label="Getting Started" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Form.Item name="tags" label="Tags (comma-separated)">
              <Input placeholder="e.g. billing, timesheet, getting-started" />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default AdminHelpCenter;
